// Quote graphics (FR-069-FR-073), drawn on a canvas from a testimonial's public text and consented
// details only. Text is fitted by shrinking, never by cutting words; a quote too long for the
// format reports `fits: false` so the user can shorten it with a "…" excerpt first.
import { onColor, textColor } from './color';

export const FORMATS = {
  square: { label: 'Square post', width: 1080, height: 1080, note: '1080 × 1080' },
  portrait: { label: 'Portrait post', width: 1080, height: 1350, note: '1080 × 1350' },
  website: { label: 'Website quote', width: 1200, height: 630, note: '1200 × 630' },
  card: { label: 'Simple card', width: 1200, height: 800, note: '1200 × 800' }
};
export const TEMPLATES = {
  paper: { label: 'Paper', note: 'Warm paper, serif quote' },
  bold: { label: 'Bold', note: 'Your brand colour' },
  minimal: { label: 'Minimal', note: 'White with a thin rule' }
};
const DISPLAY = 'Fraunces, Georgia, serif';
const BODY = '"Plus Jakarta Sans", system-ui, sans-serif';
const STAR = 'M12 2.5l2.94 5.96 6.56.95-4.75 4.63 1.12 6.54L12 17.5l-5.87 3.08 1.12-6.54L2.5 9.41l6.56-.95z';
const MARK = 'Made with TrustClap';

export async function fontsReady() {
  try {
    await Promise.all([document.fonts.load('500 64px Fraunces'), document.fonts.load('700 32px "Plus Jakarta Sans"'),
      document.fonts.load('500 28px "Plus Jakarta Sans"')]);
    await document.fonts.ready;
  } catch (error) { /* fall back to the system faces */ }
}

function wrap(context, text, width) {
  const lines = [];
  String(text).split(/\n+/).forEach(paragraph => {
    const words = paragraph.split(/\s+/).filter(Boolean);
    let line = '';
    words.forEach(word => {
      const next = line ? line + ' ' + word : word;
      if (context.measureText(next).width <= width || !line) line = next;
      else { lines.push(line); line = word; }
    });
    if (line) lines.push(line);
  });
  return lines;
}

// The largest size at which every word fits the box; null when even the smallest is too big.
function fit(context, text, width, height, largest, smallest) {
  for (let size = largest; size >= smallest; size -= 2) {
    context.font = '500 ' + size + 'px ' + DISPLAY;
    const lines = wrap(context, text, width);
    const tooWide = lines.some(line => context.measureText(line).width > width);
    if (!tooWide && lines.length * size * 1.32 <= height) return { size, lines };
  }
  return null;
}

function stars(context, x, y, size, rating, filled, empty) {
  const path = new Path2D(STAR);
  for (let i = 0; i < 5; i++) {
    context.save();
    context.translate(x + i * (size + 6), y);
    context.scale(size / 24, size / 24);
    context.fillStyle = i < rating ? filled : empty;
    context.fill(path);
    context.restore();
  }
}

function roundedRect(context, x, y, w, h, r) {
  context.beginPath();
  context.moveTo(x + r, y);
  context.arcTo(x + w, y, x + w, y + h, r);
  context.arcTo(x + w, y + h, x, y + h, r);
  context.arcTo(x, y + h, x, y, r);
  context.arcTo(x, y, x + w, y, r);
  context.closePath();
}

function palette(template, brandColor) {
  if (template === 'bold') {
    const ink = onColor(brandColor);
    const light = ink === '#FFFFFF';
    return { background: brandColor, text: ink, muted: light ? 'rgba(255,255,255,.82)' : 'rgba(31,35,40,.78)',
      mark: light ? 'rgba(255,255,255,.22)' : 'rgba(31,35,40,.16)', star: ink, starOff: light ? 'rgba(255,255,255,.35)' : 'rgba(31,35,40,.25)' };
  }
  if (template === 'minimal') {
    return { background: '#FFFFFF', text: '#1F2328', muted: '#5F5A55', mark: textColor(brandColor), star: '#B7791F', starOff: '#D4C9B8',
      rule: brandColor };
  }
  return { background: '#FAF7F2', text: '#1F2328', muted: '#5F5A55', mark: 'rgba(180,71,43,.9)', star: '#B7791F', starOff: '#D4C9B8',
    panel: '#FFFFFF', line: '#E6DED2' };
}

/**
 * Draws one graphic. `card` is {text, name, role, rating, source}; returns {fits}. An imported testimonial's source
 * always shows, after the job title and company, as it does on widgets and walls.
 * Free plans pass marked: true for the small "Made with TrustClap" mark.
 */
export async function renderQuoteCard(canvas, { format = 'square', template = 'paper', card, brandName, brandColor, marked }) {
  await fontsReady();
  const spec = FORMATS[format] || FORMATS.square;
  const colors = palette(template, brandColor || '#B4472B');
  canvas.width = spec.width;
  canvas.height = spec.height;
  const context = canvas.getContext('2d');
  const W = spec.width;
  const H = spec.height;
  const landscape = W > H;
  const pad = Math.round(Math.min(W, H) * (landscape ? 0.085 : 0.09));

  context.fillStyle = colors.background;
  context.fillRect(0, 0, W, H);
  let inset = pad;
  if (template === 'paper') {
    const margin = Math.round(pad * 0.45);
    context.fillStyle = colors.panel;
    roundedRect(context, margin, margin, W - margin * 2, H - margin * 2, 36);
    context.fill();
    context.strokeStyle = colors.line;
    context.lineWidth = 3;
    context.stroke();
    inset = pad + margin * 0.4;
  }
  if (template === 'minimal') {
    context.fillStyle = colors.rule;
    context.fillRect(pad, pad, Math.round(W * 0.12), 8);
  }

  // The oversized quotation mark.
  context.fillStyle = colors.mark;
  context.font = '600 ' + Math.round(H * (landscape ? 0.36 : 0.3)) + 'px ' + DISPLAY;
  context.textBaseline = 'top';
  context.fillText('“', inset - 8, inset - Math.round(H * 0.025) + (template === 'minimal' ? 24 : 0));

  // The byline is laid out from the bottom up, so it always stays inside the frame: role, name, stars. The bottom
  // line belongs to the role (left) and the Free mark (right), so the brand name on the name line never shares a
  // line with the mark, with or without a role.
  const unit = Math.min(W, H);
  const nameSize = Math.round(unit * 0.034);
  const roleSize = Math.round(nameSize * 0.82);
  const starSize = Math.round(unit * 0.032);
  const bottom = H - inset - Math.round(unit * 0.01);
  const detail = [card.role, card.source].filter(Boolean).join(' · ');
  const roleLine = detail ? bottom : null;
  const nameLine = detail || marked ? bottom - Math.round(roleSize * 1.55) : bottom;
  const starsTop = card.rating ? nameLine - nameSize - Math.round(starSize * 0.75) - starSize : null;
  const bylineTop = starsTop !== null ? starsTop : nameLine - nameSize;

  const top = inset + Math.round(H * (landscape ? 0.17 : 0.15));
  const boxWidth = W - inset * 2;
  const boxHeight = bylineTop - Math.round(unit * 0.06) - top;
  const largest = Math.round(unit * (landscape ? 0.075 : 0.07));
  const fitted = fit(context, card.text, boxWidth, boxHeight, largest, Math.round(largest * 0.45));
  if (!fitted) return { fits: false };

  context.fillStyle = colors.text;
  context.textBaseline = 'alphabetic';
  context.font = '500 ' + fitted.size + 'px ' + DISPLAY;
  // The quote sits in the middle of its box, so short quotes don't leave a gap above the byline.
  const start = top + Math.max(0, (boxHeight - fitted.lines.length * fitted.size * 1.32) / 2);
  fitted.lines.forEach((line, index) => context.fillText(line, inset, start + fitted.size * (index + 1) * 1.32 - fitted.size * 0.32));

  if (starsTop !== null) stars(context, inset, starsTop, starSize, card.rating, colors.star, colors.starOff);

  // Brand name on the right of the name line; on Free, the small mark on the bottom line. The left side gets the
  // width the right side leaves, shrinking (and only then shortening) a long name or role rather than overlapping.
  const gap = Math.round(unit * 0.03);
  const brand = fitLine(context, '600', Math.round(nameSize * 0.8), brandName || '', boxWidth * 0.45);
  const mark = marked ? fitLine(context, '500', Math.round(nameSize * 0.64), MARK, boxWidth * 0.45) : null;
  const name = fitLine(context, '700', nameSize, card.name || 'A customer of ' + brandName,
    boxWidth - (brand.width ? brand.width + gap : 0));
  context.fillStyle = colors.text;
  context.font = name.font;
  context.fillText(name.text, inset, nameLine);
  if (roleLine !== null) {
    const role = fitLine(context, '500', roleSize, detail, boxWidth - (mark ? mark.width + gap : 0));
    context.fillStyle = colors.muted;
    context.font = role.font;
    context.fillText(role.text, inset, roleLine);
  }
  context.textAlign = 'right';
  context.fillStyle = colors.muted;
  context.font = brand.font;
  context.fillText(brand.text, W - inset, nameLine);
  if (mark) {
    context.font = mark.font;
    context.fillText(mark.text, W - inset, bottom);
  }
  context.textAlign = 'left';
  return { fits: true };
}

// One byline line within `width`: the full text at `size` when it fits, else down to 60% of it, and only then
// shortened with "…". Returns the font to draw with, the text and its width.
function fitLine(context, weight, size, text, width) {
  const smallest = Math.round(size * 0.6);
  let font = '';
  for (let current = size; current >= smallest; current -= 1) {
    font = weight + ' ' + current + 'px ' + BODY;
    context.font = font;
    const measured = context.measureText(text).width;
    if (measured <= width) return { font, text, width: measured };
  }
  let shortened = String(text);
  while (shortened.length > 1 && context.measureText(shortened.trim() + '…').width > width) shortened = shortened.slice(0, -1);
  shortened = shortened.trim() + '…';
  return { font, text: shortened, width: context.measureText(shortened).width };
}

export function canvasBlob(canvas) {
  return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
}
